import { useState, useCallback } from 'react';
import { acknowledgeServiceRequestApiService } from '../api/acknowledgeServiceRequestApiService';
import { ACKNOWLEDGE_SR_ERROR_MESSAGES } from '../errors/errorMessages';

interface UseAcknowledgeServiceRequestOptions {
  serviceRequestId: string | undefined;
  onSuccess: (status: 'accepted' | 'rejected') => void;
  onPermissionDenied: () => void;
  onWindowExpired: () => void;
}

export interface UseAcknowledgeServiceRequestResult {
  isAcceptOpen: boolean;
  openAccept: () => void;
  closeAccept: () => void;
  isRejectOpen: boolean;
  openReject: () => void;
  closeReject: () => void;
  reason: string;
  setReason: (value: string) => void;
  reasonError: string | null;
  isSubmitting: boolean;
  isAccepting: boolean;
  isRejecting: boolean;
  submitError: string | null;
  canSubmit: boolean;
  submitAccept: () => Promise<void>;
  submitReject: () => Promise<void>;
}

/**
 * Manages the full accept/reject acknowledgement flow for an assigned service request.
 *
 * Exposes separate open/close state for the accept and reject modals, form state
 * for the rejection reason field, and submit handlers for both actions.
 *
 * On HTTP 403 the hook calls `onPermissionDenied` instead of setting `submitError`
 * so the parent can show a dedicated snackbar and refetch the SR to hide the buttons.
 *
 * On HTTP 409 (window expired) or any other API error, `submitError` is set so the
 * modal can display it inline. An unrecognised error code falls back to `DEFAULT_ERROR`.
 *
 * @param options.serviceRequestId - UUID of the SR to acknowledge.
 * @param options.onSuccess - Called with the chosen status after a successful API response.
 * @param options.onPermissionDenied - Called when the backend returns 403.
 */
export const useAcknowledgeServiceRequest = ({
  serviceRequestId,
  onSuccess,
  onPermissionDenied,
  onWindowExpired,
}: UseAcknowledgeServiceRequestOptions): UseAcknowledgeServiceRequestResult => {
  const [isAcceptOpen, setIsAcceptOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [reason, setReasonRaw] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  const setReason = useCallback((value: string) => {
    setReasonRaw(value);
    setReasonError(null);
  }, []);
  const [submittingAction, setSubmittingAction] = useState<
    'accept' | 'reject' | null
  >(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetRejectForm = useCallback(() => {
    setReasonRaw('');
    setReasonError(null);
    setSubmitError(null);
  }, []);

  const openAccept = useCallback(() => {
    setSubmitError(null);
    setIsAcceptOpen(true);
  }, []);

  const closeAccept = useCallback(() => {
    setIsAcceptOpen(false);
    setSubmitError(null);
  }, []);

  const openReject = useCallback(() => {
    resetRejectForm();
    setIsRejectOpen(true);
  }, [resetRejectForm]);

  const closeReject = useCallback(() => {
    setIsRejectOpen(false);
    resetRejectForm();
  }, [resetRejectForm]);

  const resolveErrorMessage = (result: {
    windowExpired?: boolean;
    isNetworkError?: boolean;
    status?: number;
  }): string => {
    if (result.windowExpired) {
      return ACKNOWLEDGE_SR_ERROR_MESSAGES.ACTION_WINDOW_EXPIRED;
    }
    if (result.isNetworkError) {
      return ACKNOWLEDGE_SR_ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (result.status === 500) {
      return ACKNOWLEDGE_SR_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
    }
    return ACKNOWLEDGE_SR_ERROR_MESSAGES.DEFAULT_ERROR;
  };

  const submitAccept = useCallback(async () => {
    if (!serviceRequestId) {
      return;
    }
    setSubmittingAction('accept');
    setSubmitError(null);

    const result = await acknowledgeServiceRequestApiService(serviceRequestId, {
      status: 'accepted',
    });

    setSubmittingAction(null);

    if (result.permissionDenied) {
      setIsAcceptOpen(false);
      onPermissionDenied();
      return;
    }

    if (result.windowExpired) {
      setIsAcceptOpen(false);
      onWindowExpired();
      return;
    }

    if (result.error) {
      setSubmitError(resolveErrorMessage(result));
      return;
    }

    setIsAcceptOpen(false);
    onSuccess('accepted');
  }, [serviceRequestId, onSuccess, onPermissionDenied, onWindowExpired]);

  const submitReject = useCallback(async () => {
    if (!serviceRequestId) {
      return;
    }

    if (!reason.trim()) {
      setReasonError(ACKNOWLEDGE_SR_ERROR_MESSAGES.REASON_REQUIRED);
      return;
    }
    if (reason.length > 500) {
      setReasonError(ACKNOWLEDGE_SR_ERROR_MESSAGES.REASON_TOO_LONG);
      return;
    }

    setSubmittingAction('reject');
    setSubmitError(null);

    const result = await acknowledgeServiceRequestApiService(serviceRequestId, {
      status: 'rejected',
      reason: reason.trim(),
    });

    setSubmittingAction(null);

    if (result.permissionDenied) {
      setIsRejectOpen(false);
      resetRejectForm();
      onPermissionDenied();
      return;
    }

    if (result.windowExpired) {
      setIsRejectOpen(false);
      resetRejectForm();
      onWindowExpired();
      return;
    }

    if (result.error) {
      setSubmitError(resolveErrorMessage(result));
      return;
    }

    setIsRejectOpen(false);
    resetRejectForm();
    onSuccess('rejected');
  }, [
    serviceRequestId,
    reason,
    onSuccess,
    onPermissionDenied,
    onWindowExpired,
    resetRejectForm,
  ]);

  const isSubmitting = submittingAction !== null;
  return {
    isAcceptOpen,
    openAccept,
    closeAccept,
    isRejectOpen,
    openReject,
    closeReject,
    reason,
    setReason,
    reasonError,
    isSubmitting,
    isAccepting: submittingAction === 'accept',
    isRejecting: submittingAction === 'reject',
    submitError,
    canSubmit: !isSubmitting,
    submitAccept,
    submitReject,
  };
};
